
import { useEffect, useState } from 'react';

interface UseAnimatedCounterOptions {
  target: number;
  duration?: number;
  enabled?: boolean;
  delay?: number;
}

export const useAnimatedCounter = ({ 
  target, 
  duration = 400, 
  enabled = true,
  delay = 0
}: UseAnimatedCounterOptions) => {
  const [current, setCurrent] = useState(target);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousTarget, setPreviousTarget] = useState(target);

  useEffect(() => {
    if (!enabled || target === previousTarget) return;
    
    setPreviousTarget(target);

    const startAnimation = () => {
      setIsAnimating(true);
      const startValue = current;
      const difference = target - startValue;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const newValue = Math.round(startValue + difference * easeOutQuart);
        
        setCurrent(newValue);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    };

    if (delay > 0) {
      const timeoutId = setTimeout(startAnimation, delay);
      return () => clearTimeout(timeoutId);
    } else {
      startAnimation();
    }
  }, [target, duration, enabled, delay, previousTarget]);

  return { current, isAnimating };
};
